import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_BACKOFF_MS = 10_000;

/**
 * Single WebSocket to the existing chat server (`ws://<host>/ws/chat`).
 *
 * This is the platform's raw `ws` server, NOT Socket.io - see
 * server/src/lib/ws.manager.ts. It authenticates from the same httpOnly cookie
 * on the upgrade request, which the browser sends for same-origin connections.
 *
 * Server → client events: `connected`, `new_message`, `typing`, `pin_update`.
 * Client → server:          `{ type: 'typing', conversationId, isTyping }`.
 *
 * One socket for the whole app; consumers subscribe to events rather than each
 * opening their own connection.
 */
export function useChatSocket({ enabled }) {
  const [status, setStatus] = useState('idle');
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return undefined;
    }

    let disposed = false;
    let attempt = 0;
    let retryTimer;

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(`${protocol}//${window.location.host}/ws/chat`);
      socketRef.current = socket;
      setStatus('connecting');

      socket.addEventListener('open', () => {
        attempt = 0;
        setStatus('open');
      });

      socket.addEventListener('message', (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        listenersRef.current.forEach((listener) => listener(payload));
      });

      socket.addEventListener('close', () => {
        if (disposed) return;
        setStatus('closed');
        attempt += 1;
        // Exponential backoff, capped - the server restarts during deploys.
        retryTimer = setTimeout(connect, Math.min(2 ** attempt * 250, MAX_BACKOFF_MS));
      });

      socket.addEventListener('error', () => socket.close());
    };

    connect();

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled]);

  /** Typing is fire-and-forget; the socket also validates access server-side. */
  const sendTyping = useCallback((conversationId, isTyping) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'typing', conversationId, isTyping }));
    }
  }, []);

  return { status, subscribe, sendTyping };
}
